import React, { createContext, useState, useEffect, ReactNode } from "react";
import { AuthService } from "../authentication/service";
import { ErrorToast } from "../shared/toasters/toasters";

// Define the context for authentication state
export const AuthContext = createContext<{
    user: User | null;
    setUser: React.Dispatch<React.SetStateAction<User | null>>;
    login: (body: any) => Promise<void>;
    logout: () => void;
    loading: boolean;
}>({
    user: null,
    setUser: () => { },
    login: async () => { },
    logout: () => { },
    loading: false,
});

interface AuthProviderProps {
    children: ReactNode;
}

export default interface IContactInfo {
    phone: string;
    city: string;
    zip: string;
    address: string;
    addressSuffix: string;
}

// Define the User type
export interface User {
    id: number;
    createdAt: string;
    updatedAt: string;
    email: string;
    firstName: string;
    contact: IContactInfo;
    lastName: string;
    isActive: boolean;
    roles: string[];
    company: string | null;
    location: string | null;
    profilePicture: {
        id: number;
        createdAt: string;
        updatedAt: string;
        file: {
            name: string;
            nameOriginal: string;
            mime: string;
            size: number;
        };
        path: string;
    };
}

export interface AuthContextType {
    user: User | null;
    setUser: React.Dispatch<React.SetStateAction<User | null>>;
    login: (credentials: { email: string; password: string }) => Promise<void>;
    logout: () => void;
    loading: boolean;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const isAdmin = (roles: string[]) => {
        const adminRoles = ["ROLE_ADMIN"];
        return roles.some(role => adminRoles.includes(role));
    };

    useEffect(() => {
        if(isAdmin) {
            

        const token = localStorage.getItem("token");

        if (token) {
            AuthService.getMe()
                .then((response) => {
                    if (!isAdmin(response.roles)) {
                        logout();
                    } else {
                        setUser(response);
                    }
                })
                .catch(() => {
                    logout();
                })
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }
    }, []);


    const login = async (body: any) => {
        try {
          const response = await AuthService.login({ email: body.email, password: body.password });
          const { token, user } = response;
      
          if (isAdmin(user.roles)) {
  
            localStorage.setItem("token", token); // Store token in localStorage
            setUser(user); // Set user state
          } else {
            localStorage.removeItem("token"); // Remove token if not authorized
            localStorage.removeItem("currentUser"); // Remove user from localStorage
            throw new Error("You are not authorized to access this application");
          }
        } catch (error: any) {
   
          if (error.response?.data?.message === "auth.bad_credential") {
            ErrorToast("Invalid email or password. Please try again."); // Show a user-friendly error message
          } else {
            ErrorToast(error.message || "An unexpected error occurred. Please try again later."); // Handle other errors
          }
          throw error; // Re-throw the error if needed
        }
      };
    const logout = () => {
        localStorage.removeItem("token");
        setUser(null);
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <AuthContext.Provider value={{ user, setUser, login, logout, loading }}>
            {children} {/* Render child components */}
        </AuthContext.Provider>
    );
};